import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

export default class extends Controller {
  static targets = [
    "countLabel",
    "deleteButtonLabel",
    "editLink",
    "footerModalBody",
    "footerModalOverlay",
    "footerModalTemplateDescription",
    "footerModalTemplateTitle",
    "footerPreviewFrame",
    "footerPreviewFrameWrap",
    "footerSidebarContent",
    "footerSummaryCopyright",
    "footerSummaryLogo",
    "footerSummaryTitle",
    "headerCtaTextInput",
    "headerCtaUrlInput",
    "headerLogoInput",
    "headerMenuInput",
    "headerModalOverlay",
    "headerModalBody",
    "headerModalTemplateDescription",
    "headerModalTemplateTitle",
    "headerPreviewFrame",
    "headerPreviewFrameWrap",
    "headerShowInput",
    "headerSidebarContent",
    "headerSummaryCta",
    "headerSummaryLogo",
    "headerSummaryTitle",
    "heroCtaTextInput",
    "heroModalBody",
    "heroModalOverlay",
    "heroModalTemplateDescription",
    "heroModalTemplateTitle",
    "heroNameInput",
    "heroPreviewFrame",
    "heroPreviewFrameWrap",
    "heroStyleAction",
    "heroTaglineInput",
    "heroTitleInput",
    "hiddenFields",
    "inspectorFields",
    "previewFrame",
    "replaceAction",
    "replaceModalBody",
    "replaceModalOverlay",
    "replaceModalTemplateDescription",
    "replaceModalTemplateTitle",
    "sidebarContent",
    "sidebarEmpty",
    "sortableList",
    "titleInput",
    "typeSelect",
    "visibleInput"
  ]

  static values = {
    editUrlTemplate: String,
    footer: String,
    footerTemplates: String,
    header: String,
    headerTemplates: String,
    heroTemplates: String,
    sectionTemplates: String,
    previewUrl: String,
    schema: String,
    selectedSurface: String,
    sections: String,
    selectedKey: String,
    msgHeroModalTitle: { type: String, default: "히어로 템플릿 미리보기" },
    msgHeroModalDescription: { type: String, default: "선택한 히어로 템플릿을 이 영역에서 확인할 수 있습니다." },
    msgHeaderModalTitle: { type: String, default: "헤더 템플릿 미리보기" },
    msgHeaderModalDescription: { type: String, default: "선택한 헤더 템플릿을 이 영역에서 확인할 수 있습니다." },
    msgFooterModalTitle: { type: String, default: "푸터 템플릿 미리보기" },
    msgFooterModalDescription: { type: String, default: "선택한 푸터 템플릿을 이 영역에서 확인할 수 있습니다." },
    msgPreviewRefreshError: { type: String, default: "미리보기를 새로고침할 수 없습니다." },
    msgDeleteLabel: { type: String, default: "삭제" },
    msgDeleteCancelLabel: { type: String, default: "삭제 취소" },
    msgHeaderStyleFull: { type: String, default: "풀 헤더" },
    msgHeaderStyleSimple: { type: String, default: "심플 헤더" },
    msgHeaderStyleNone: { type: String, default: "헤더 없음" },
    msgFooterStyleFull: { type: String, default: "풀 푸터" },
    msgFooterStyleSimple: { type: String, default: "심플 푸터" },
    msgFooterStyleNone: { type: String, default: "푸터 없음" },
    msgLogoNotSet: { type: String, default: "미설정" },
    msgNoCopyright: { type: String, default: "저작권 없음" },
    msgFieldAddLabel: { type: String, default: "항목 추가" },
    msgSectionFallback: { type: String, default: "섹션" },
    msgSectionTypeFallback: { type: String, default: "선택한 섹션" }
  }

  connect() {
    this.submitting = false
    this.previewTimer = null
    this.previewRequestId = 0
    this.footerModalPreviewTemplateId = null
    this.footerModalDevice = "pc"
    this.footerModalOpen = false
    this.headerModalPreviewTemplateId = null
    this.headerModalDevice = "pc"
    this.headerModalOpen = false
    this.heroTemplates = this.parseJson(this.heroTemplatesValue, [])
    this.heroModalPreviewTemplateId = null
    this.heroModalDevice = "pc"
    this.heroModalOpen = false
    this.heroModalSectionKey = null
    this.activeInlineFieldPath = null
    this.activeInlineSectionKey = null
    this.activeInspectorFieldPath = null
    this.pendingInlineFocus = null
    this.replaceModalOpen = false
    this.replaceModalSectionKey = null
    this.replaceModalPreviewTemplateId = null
    this.nextTempId = 1
    this.schema = this.parseJson(this.schemaValue, {})
    this.footer = this.normalizeFooter(this.parseJson(this.footerValue, {}))
    this.footerTemplates = this.parseJson(this.footerTemplatesValue, [])
    this.header = this.normalizeHeader(this.parseJson(this.headerValue, {}))
    this.headerTemplates = this.parseJson(this.headerTemplatesValue, [])
    this.sectionTemplates = this.parseJson(this.sectionTemplatesValue, [])
    this.sections = this.parseJson(this.sectionsValue, []).map((section, index) => this.normalizeSection(section, index))
    this.labels = this.schema.labels || {}
    this.sectionTypes = this.uniqueTypes()
    this.selectedKey = this.selectedKeyValue || this.sections.find((section) => !section._destroy)?.client_key || null
    this.selectedSurface = this.selectedSurfaceValue || (this.selectedKey ? "section" : "header")
    this.initialSnapshot = this.snapshot()

    this.sections.forEach((section) => this.bumpTempId(section.client_key))
    this.setupStaticBindings()
    this.renderInspector()
    this.renderHeaderModal()
    this.renderFooterModal()
    this.renderHeroModal()
    this.syncHiddenFields()
    this.emitSelectionChanged("initial")
    this.updateCount()
    this.setupSortable()
    window.addEventListener("beforeunload", this.beforeUnload)
  }

  disconnect() {
    this.teardownSortable()
    this.removeStaticBindings()
    window.removeEventListener("beforeunload", this.beforeUnload)
    window.clearTimeout(this.previewTimer)
    document.body.style.overflow = ""
  }

  refreshPreview(event) {
    event?.preventDefault()
    this.queuePreviewRefresh({ immediate: true })
  }

  openPicker(event) {
    event.preventDefault()
    event.stopPropagation()

    const insertIndex = Number(event.currentTarget.dataset.insertIndex || 0)
    window.dispatchEvent(new CustomEvent("profile-page-designer:open-picker", {
      detail: { insertIndex }
    }))
  }

  receivePick(event) {
    const insertIndex = Number(event.detail?.insertIndex ?? this.visibleSections().length)
    const sectionType = event.detail?.sectionType?.toString()
    if (!sectionType) return

    const section = this.buildNewSection(sectionType)
    this.sections.splice(insertIndex, 0, section)
    this.normalizePositions()
    this.selectedSurface = "section"
    this.selectedKey = section.client_key
    this.pendingInlineFocus = { clientKey: section.client_key }
    this.renderInspector()
    this.syncHiddenFields()
    this.updateCount()
    this.emitSelectionChanged("picker")
    this.queuePreviewRefresh({ immediate: true })
  }

  selectSection(event) {
    if (event.target.closest("[data-inline-editable='true']")) return

    const key = event.currentTarget.dataset.clientKey
    if (!key) return

    this.selectedSurface = "section"
    this.selectedKey = key
    this.renderInspector()
    this.applySelectionState()
    this.scrollSelectionIntoView()
    this.emitSelectionChanged("user")
  }

  selectHeader(event) {
    if (event.target.closest("button, a, input, select, textarea")) return

    event.preventDefault()
    this.selectedSurface = "header"
    this.renderInspector()
    this.applySelectionState()
    this.scrollSelectionIntoView()
    this.emitSelectionChanged("user")
  }

  selectFooter(event) {
    if (event.target.closest("button, a, input, select, textarea")) return

    event.preventDefault()
    this.selectedSurface = "footer"
    this.renderInspector()
    this.applySelectionState()
    this.scrollSelectionIntoView()
    this.emitSelectionChanged("user")
  }

  openSectionReplaceModal(event) {
    event?.preventDefault()
    event?.stopPropagation()

    const key = event?.currentTarget?.dataset?.clientKey || this.selectedKey
    const section = key ? this.sectionByKey(key) : this.selectedSection()
    if (!section || section.section_type === "hero" || section._destroy) return

    this.selectedSurface = "section"
    this.selectedKey = section.client_key
    this.replaceModalSectionKey = section.client_key
    this.replaceModalPreviewTemplateId = null
    this.closeHeaderModal()
    this.closeFooterModal()
    this.closeHeroModal()
    this.replaceModalOpen = true
    this.renderInspector()
    this.renderSectionReplaceModal()
    this.emitSelectionChanged("user")

    if (this.hasReplaceModalOverlayTarget) {
      this.replaceModalOverlayTarget.classList.add("is-open")
      document.body.style.overflow = "hidden"
    }
  }

  closeSectionReplaceModal(event) {
    event?.preventDefault()
    this.replaceModalOpen = false
    this.replaceModalSectionKey = null
    this.replaceModalPreviewTemplateId = null
    if (this.hasReplaceModalOverlayTarget) {
      this.replaceModalOverlayTarget.classList.remove("is-open")
      document.body.style.overflow = ""
    }
  }

  closeSectionReplaceModalOnBackdrop(event) {
    if (event.target === this.replaceModalOverlayTarget) {
      this.closeSectionReplaceModal()
    }
  }

  openHeaderModal(event) {
    event?.preventDefault()
    event?.stopPropagation()

    this.selectedSurface = "header"
    this.headerModalOpen = true
    this.ensureHeaderModalPreviewTemplate()
    this.renderInspector()
    this.renderHeaderModal()
    this.emitSelectionChanged("user")
    if (this.hasHeaderModalOverlayTarget) {
      this.headerModalOverlayTarget.classList.add("is-open")
      document.body.style.overflow = "hidden"
    }
  }

  closeHeaderModal(event) {
    event?.preventDefault()
    this.headerModalOpen = false
    if (this.hasHeaderModalOverlayTarget) {
      this.headerModalOverlayTarget.classList.remove("is-open")
      document.body.style.overflow = ""
    }
  }

  closeHeaderModalOnBackdrop(event) {
    if (event.target === this.headerModalOverlayTarget) {
      this.closeHeaderModal()
    }
  }

  openFooterModal(event) {
    event?.preventDefault()
    event?.stopPropagation()

    this.selectedSurface = "footer"
    this.footerModalOpen = true
    this.ensureFooterModalPreviewTemplate()
    this.renderInspector()
    this.renderFooterModal()
    this.emitSelectionChanged("user")
    if (this.hasFooterModalOverlayTarget) {
      this.footerModalOverlayTarget.classList.add("is-open")
      document.body.style.overflow = "hidden"
    }
  }

  closeFooterModal(event) {
    event?.preventDefault()
    this.footerModalOpen = false
    if (this.hasFooterModalOverlayTarget) {
      this.footerModalOverlayTarget.classList.remove("is-open")
      document.body.style.overflow = ""
    }
  }

  closeFooterModalOnBackdrop(event) {
    if (event.target === this.footerModalOverlayTarget) {
      this.closeFooterModal()
    }
  }

  selectHeaderTemplate(event) {
    event.preventDefault()
    const templateId = event.currentTarget.dataset.headerTemplateId
    const template = this.headerTemplateById(templateId)
    if (!template) return

    this.headerModalPreviewTemplateId = String(template.id)
    this.header.show_header = true
    this.header.header_style = template.header_style
    this.header.template_id = String(template.id)
    this.header.header_data = this.deepClone(template.header_data || {})
    this.header.header_data.template_id = String(template.id)
    if (this.header.header_data.menu_enabled == null) this.header.header_data.menu_enabled = true

    this.renderHeaderModal()
    this.renderInspector()
    this.syncHiddenFields()
    this.queuePreviewRefresh({ immediate: true })
  }

  switchHeaderPreviewDevice(event) {
    event.preventDefault()
    this.headerModalDevice = event.currentTarget.dataset.device || "pc"
    this.renderHeaderModal()
  }

  selectFooterTemplate(event) {
    event.preventDefault()
    const templateId = event.currentTarget.dataset.footerTemplateId
    const template = this.footerTemplateById(templateId)
    if (!template) return

    this.footerModalPreviewTemplateId = String(template.id)
    this.footer.show_footer = true
    this.footer.footer_style = template.footer_style
    this.footer.template_id = String(template.id)
    this.footer.footer_data = this.deepClone(template.footer_data || {})
    this.footer.footer_data.template_id = String(template.id)

    this.renderFooterModal()
    this.renderInspector()
    this.syncHiddenFields()
    this.queuePreviewRefresh({ immediate: true })
  }

  selectSectionReplacementTemplate(event) {
    event.preventDefault()
    event.stopPropagation()

    const templateId = event.currentTarget.dataset.sectionTemplateId
    const template = this.sectionTemplateById(templateId)
    if (!template) return

    this.applySectionReplacementTemplate(template)
    this.closeSectionReplaceModal()
  }

  switchFooterPreviewDevice(event) {
    event.preventDefault()
    this.footerModalDevice = event.currentTarget.dataset.device || "pc"
    this.renderFooterModal()
  }

  updateTitle = (event) => {
    const section = this.selectedSection()
    if (!section) return

    section.title = event.currentTarget.value
    this.syncHiddenFields()
    this.updateCount()
    this.emitSelectionChanged()
    this.queuePreviewRefresh()
  }

  updateType = (event) => {
    const section = this.selectedSection()
    if (!section) return

    const nextType = event.currentTarget.value
    const previousType = section.section_type
    const previousTitle = (section.title || "").trim()

    section.section_type = nextType
    section.content_data = this.deepClone(this.starterContentFor(nextType))

    if (!previousTitle || previousTitle === this.labelFor(previousType)) {
      section.title = this.labelFor(nextType)
    }

    this.renderInspector()
    this.syncHiddenFields()
    this.updateCount()
    this.emitSelectionChanged()
    this.queuePreviewRefresh({ immediate: true })
  }

  updateVisible = (event) => {
    const section = this.selectedSection()
    if (!section) return

    section.visible = event.currentTarget.checked
    this.syncHiddenFields()
    this.updateCount()
    this.emitSelectionChanged()
    this.queuePreviewRefresh()
  }

  updateField(event) {
    const input = event.target
    const path = input.dataset.path
    if (!path) return

    const section = this.selectedSection()
    if (!section) return

    this.selectedSurface = "section"
    const value = input.type === "checkbox" ? input.checked : input.value
    this.setNestedValue(section.content_data, path, value)
    this.syncHiddenFields()
    this.emitSelectionChanged()
    this.queuePreviewRefresh()
  }

  updateHeaderField(event) {
    const input = event.target
    const path = input.dataset.headerPath
    if (!path) return

    this.selectedSurface = "header"
    const value = input.type === "checkbox" ? input.checked : input.value
    this.setNestedValue(this.header, path, value)

    if (path === "show_header" && !value) {
      this.header.show_header = false
    }

    if (path === "header_data.menu_enabled" && this.header.header_data?.menu_enabled == null) {
      this.header.header_data.menu_enabled = true
    }

    this.renderInspector()
    this.renderHeaderModal()
    this.syncHiddenFields()
    this.queuePreviewRefresh()
  }

  updateFooterField(event) {
    const input = event.target
    const path = input.dataset.footerPath
    if (!path) return

    this.selectedSurface = "footer"
    const value = input.type === "checkbox" ? input.checked : input.value
    this.setNestedValue(this.footer, path, value)

    if (path === "show_footer" && !value) {
      this.footer.show_footer = false
    }

    this.renderInspector()
    this.renderFooterModal()
    this.syncHiddenFields()
    this.queuePreviewRefresh()
  }

  openHeroModal(event) {
    event?.preventDefault()
    event?.stopPropagation()

    const key = event?.currentTarget?.dataset?.clientKey || this.selectedKey
    if (!key) return

    this.heroModalSectionKey = key
    this.selectedKey = key
    this.selectedSurface = "section"
    this.heroModalOpen = true
    this.ensureHeroModalPreviewTemplate()
    this.renderInspector()
    this.renderHeroModal()
    if (this.hasHeroModalOverlayTarget) {
      this.heroModalOverlayTarget.classList.add("is-open")
      document.body.style.overflow = "hidden"
    }
  }

  closeHeroModal(event) {
    event?.preventDefault()
    this.heroModalOpen = false
    if (this.hasHeroModalOverlayTarget) {
      this.heroModalOverlayTarget.classList.remove("is-open")
      document.body.style.overflow = ""
    }
  }

  applyHeroTemplateAndClose(event) {
    event?.preventDefault()
    const templateName = this.heroModalPreviewTemplate()?.name || null
    this.closeHeroModal()
    this.emitSelectionChanged("user")
    window.dispatchEvent(new CustomEvent("profile-page-designer:hero-template-applied", {
      detail: { templateName }
    }))
  }

  closeHeroModalOnBackdrop(event) {
    if (event.target === this.heroModalOverlayTarget) {
      this.closeHeroModal()
    }
  }

  selectHeroTemplate(event) {
    event.preventDefault()
    const templateId = event.currentTarget.dataset.heroTemplateId
    const template = this.heroTemplateById(templateId)
    if (!template) return

    const section = this.heroModalSectionKey ? this.sectionByKey(this.heroModalSectionKey) : this.selectedSection()
    if (!section) return

    this.heroModalPreviewTemplateId = String(template.id)
    section.style_variant = template.style_variant
    section.content_data = this.deepClone(template.content_data || {})

    this.renderHeroModal()
    this.renderInspector()
    this.syncHiddenFields()
    this.queuePreviewRefresh({ immediate: true })
  }

  switchHeroPreviewDevice(event) {
    event.preventDefault()
    this.heroModalDevice = event.currentTarget.dataset.device || "pc"
    this.renderHeroModal()
  }

  updateHeroField(event) {
    const input = event.target
    const path = input.dataset.heroPath
    if (!path) return

    const section = this.heroModalSectionKey ? this.sectionByKey(this.heroModalSectionKey) : this.selectedSection()
    if (!section) return

    const value = input.type === "checkbox" ? input.checked : input.value
    this.setNestedValue(section, path, value)

    this.renderInspector()
    this.renderHeroModal()
    this.syncHiddenFields()
    this.queuePreviewRefresh()
  }

  renderHeroModal() {
    const section = this.heroModalSectionKey ? this.sectionByKey(this.heroModalSectionKey) : this.selectedSection()
    const contentData = section?.content_data || {}

    if (this.hasHeroNameInputTarget) this.heroNameInputTarget.value = contentData.name || ""
    if (this.hasHeroTitleInputTarget) this.heroTitleInputTarget.value = contentData.title || ""
    if (this.hasHeroTaglineInputTarget) this.heroTaglineInputTarget.value = contentData.tagline || ""
    if (this.hasHeroCtaTextInputTarget) this.heroCtaTextInputTarget.value = contentData.cta_text || ""

    const previewTemplate = this.heroModalPreviewTemplate()
    if (this.hasHeroModalTemplateTitleTarget) {
      this.heroModalTemplateTitleTarget.textContent = previewTemplate?.name || this.msgHeroModalTitleValue
    }
    if (this.hasHeroModalTemplateDescriptionTarget) {
      this.heroModalTemplateDescriptionTarget.textContent = previewTemplate?.description || this.msgHeroModalDescriptionValue
    }
    if (this.hasHeroPreviewFrameTarget) {
      const nextSrc = previewTemplate?.preview_url || ""
      if (this.heroPreviewFrameTarget.src !== nextSrc) {
        this.heroPreviewFrameTarget.src = nextSrc
      } else {
        this.syncHeroPreviewFrameHeight()
      }
    }
    if (this.hasHeroPreviewFrameWrapTarget) {
      this.heroPreviewFrameWrapTarget.dataset.device = this.heroModalDevice
    }
    if (this.hasHeroModalBodyTarget) {
      this.heroModalBodyTarget.dataset.device = this.heroModalDevice
    }

    this.element.querySelectorAll("[data-hero-template-id]").forEach((card) => {
      card.classList.toggle("is-selected", card.dataset.heroTemplateId === String(this.heroModalPreviewTemplateId || ""))
    })

    this.element.querySelectorAll(".ppd-hero-modal__device").forEach((button) => {
      button.classList.toggle("is-active", button.dataset.device === this.heroModalDevice)
    })

    this.syncHeroPreviewFrameHeight()
  }

  ensureHeroModalPreviewTemplate() {
    if (this.heroModalPreviewTemplateId && this.heroTemplateById(this.heroModalPreviewTemplateId)) return

    const section = this.heroModalSectionKey ? this.sectionByKey(this.heroModalSectionKey) : this.selectedSection()
    const currentVariant = section?.style_variant
    if (currentVariant) {
      const match = this.heroTemplates.find((t) => t.style_variant === currentVariant)
      if (match) {
        this.heroModalPreviewTemplateId = String(match.id)
        return
      }
    }

    this.heroModalPreviewTemplateId = String(this.heroTemplates[0]?.id || "")
  }

  heroTemplateById(id) {
    if (!id) return null
    return this.heroTemplates.find((template) => String(template.id) === String(id)) || null
  }

  heroModalPreviewTemplate() {
    return this.heroTemplateById(this.heroModalPreviewTemplateId)
  }

  syncHeroPreviewFrameHeight() {
    if (!this.hasHeroPreviewFrameTarget) return

    window.requestAnimationFrame(() => {
      const frame = this.heroPreviewFrameTarget
      const doc = frame.contentDocument || frame.contentWindow?.document
      if (!doc) return

      const body = doc.body
      const html = doc.documentElement
      const documentHeight = Math.max(
        body?.scrollHeight || 0,
        body?.offsetHeight || 0,
        html?.scrollHeight || 0,
        html?.offsetHeight || 0
      )
      const nextHeight = Math.max(documentHeight, 400)
      frame.style.height = `${nextHeight}px`
    })
  }

  addRepeaterItem(event) {
    event.preventDefault()

    const section = this.selectedSection()
    if (!section) return

    const name = event.currentTarget.dataset.repeaterName
    const limit = Number(event.currentTarget.dataset.limit || 0)
    const schema = this.fieldSchemaFor(section.section_type).find((field) => field.name === name && field.type === "repeater")
    if (!schema) return

    const items = Array.isArray(section.content_data[name]) ? section.content_data[name] : []
    if (limit > 0 && items.length >= limit) return

    items.push(this.blankRepeaterItem(schema.fields || []))
    section.content_data[name] = items

    this.renderInspector()
    this.syncHiddenFields()
    this.emitSelectionChanged()
    this.queuePreviewRefresh()
  }

  removeRepeaterItem(event) {
    event.preventDefault()

    const section = this.selectedSection()
    if (!section) return

    const name = event.currentTarget.dataset.repeaterName
    const index = Number(event.currentTarget.dataset.index)
    const items = Array.isArray(section.content_data[name]) ? section.content_data[name] : []
    if (Number.isNaN(index) || index < 0 || index >= items.length) return

    items.splice(index, 1)
    section.content_data[name] = items

    this.renderInspector()
    this.syncHiddenFields()
    this.emitSelectionChanged()
    this.queuePreviewRefresh()
  }

  toggleDelete() {
    const section = this.selectedSection()
    if (!section) return

    this.removeSection(section.client_key)
  }

  deleteSectionFromCanvas(event) {
    event.preventDefault()
    event.stopPropagation()

    const key = event.currentTarget.dataset.clientKey
    if (!key) return

    this.removeSection(key)
  }

  editSectionFromCanvas(event) {
    event.preventDefault()
    event.stopPropagation()

    const key = event.currentTarget.dataset.clientKey
    if (!key) return

    this.selectedSurface = "section"
    this.selectedKey = key
    this.pendingInlineFocus = { clientKey: key, moveCaretToEnd: true }
    this.renderInspector()
    this.queuePreviewRefresh({ immediate: true })
  }

  startInlineEditingFromPreview = (event) => {
    const editable = event.target.closest("[data-inline-editable='true']")
    if (!editable || !this.previewFrameTarget.contains(editable)) return

    event.preventDefault()
    event.stopPropagation()
    this.startInlineEditing(editable, { moveCaretToEnd: false })
  }

  handleInlineBlur = (event) => {
    const editable = event.target.closest("[data-inline-editable='true']")
    if (!editable || editable.dataset.inlineEditing !== "true") return

    this.commitInlineEditing(editable)
  }

  handleInlineKeydown = (event) => {
    const editable = event.target.closest("[data-inline-editable='true']")
    if (!editable || editable.dataset.inlineEditing !== "true") return

    const multiline = editable.dataset.inlineMultiline === "true"

    if (event.key === "Escape") {
      event.preventDefault()
      this.cancelInlineEditing(editable)
      return
    }

    if (!multiline && event.key === "Enter") {
      event.preventDefault()
      editable.blur()
    }
  }

  handleInlinePaste = (event) => {
    const editable = event.target.closest("[data-inline-editable='true']")
    if (!editable || editable.dataset.inlineEditing !== "true") return

    event.preventDefault()
    const text = event.clipboardData?.getData("text/plain") || ""
    document.execCommand("insertText", false, text)
  }

  handleSubmit = () => {
    this.submitting = true
    this.syncHiddenFields()
  }

  beforeUnload = (event) => {
    if (this.submitting || !this.isDirty()) return

    event.preventDefault()
    event.returnValue = ""
  }

  async performPreviewRefresh() {
    if (!this.hasPreviewFrameTarget || !this.previewUrlValue) return

    const requestId = ++this.previewRequestId
    const formData = this.buildPreviewPayload()

    try {
      const response = await fetch(this.previewUrlValue, {
        method: "POST",
        headers: {
          "Accept": "text/html",
          "X-CSRF-Token": this.csrfToken()
        },
        credentials: "same-origin",
        body: formData
      })

      const html = await response.text()
      if (!response.ok) throw new Error(html || this.msgPreviewRefreshErrorValue)
      if (requestId !== this.previewRequestId) return

      this.teardownSortable()
      this.previewFrameTarget.innerHTML = html
      this.setupSortable()
      this.restoreInlineFocus()
      this.applySelectionState()
      this.applyLinkedFocusState()
    } catch (error) {
      console.error(error)
    }
  }

  setupSortable() {
    if (!this.hasSortableListTarget) return

    this.sortable = Sortable.create(this.sortableListTarget, {
      animation: 150,
      handle: "[data-drag-handle]",
      onEnd: () => {
        const orderedKeys = Array.from(this.sortableListTarget.querySelectorAll("[data-client-key]"))
          .map((element) => element.dataset.clientKey)
          .filter(Boolean)

        if (orderedKeys.length === 0) return

        const keyed = new Map(this.sections.map((section) => [section.client_key, section]))
        const reordered = orderedKeys.map((key) => keyed.get(key)).filter(Boolean)
        const removed = this.sections.filter((section) => !orderedKeys.includes(section.client_key))

        this.sections = [...reordered, ...removed]
        this.normalizePositions()
        this.syncHiddenFields()
      }
    })
  }

  teardownSortable() {
    this.sortable?.destroy()
    this.sortable = null
  }

  setupStaticBindings() {
    this.element.addEventListener("submit", this.handleSubmit)
    this.titleInputTarget.addEventListener("input", this.updateTitle)
    this.typeSelectTarget.addEventListener("change", this.updateType)
    this.visibleInputTarget.addEventListener("change", this.updateVisible)
    window.addEventListener("profile-page-designer:apply-section-content", this.handleApplySectionContent)
    window.addEventListener("profile-page-designer:apply-all-sections-content", this.handleApplyAllSectionsContent)
    window.addEventListener("profile-page-designer:snapshot-all-sections", this.handleSnapshotAllSections)
    window.addEventListener("profile-page-designer:request-selection", this.handleSelectionRequest)
    window.addEventListener("profile-page-designer:activate-section", this.handleActivateSection)
    window.addEventListener("profile-page-designer:restructure-page", this.handleRestructurePage)
    window.addEventListener("profile-page-designer:restore-all-sections", this.handleRestoreAllSections)
    window.addEventListener("profile-page-designer:update-images", this.handleUpdateImages)
    this.previewFrameTarget.addEventListener("dblclick", this.startInlineEditingFromPreview)
    this.previewFrameTarget.addEventListener("focusout", this.handleInlineBlur)
    this.previewFrameTarget.addEventListener("keydown", this.handleInlineKeydown)
    this.previewFrameTarget.addEventListener("paste", this.handleInlinePaste)
    this.inspectorFieldsTarget.addEventListener("focusin", this.handleInspectorFocusIn)
    this.inspectorFieldsTarget.addEventListener("focusout", this.handleInspectorFocusOut)
    if (this.hasHeaderPreviewFrameTarget) this.headerPreviewFrameTarget.addEventListener("load", this.handleHeaderPreviewLoad)
    if (this.hasFooterPreviewFrameTarget) this.footerPreviewFrameTarget.addEventListener("load", this.handleFooterPreviewLoad)
    document.addEventListener("keydown", this.handleGlobalKeydown)
  }

  removeStaticBindings() {
    this.element.removeEventListener("submit", this.handleSubmit)
    if (this.hasTitleInputTarget) this.titleInputTarget.removeEventListener("input", this.updateTitle)
    if (this.hasTypeSelectTarget) this.typeSelectTarget.removeEventListener("change", this.updateType)
    if (this.hasVisibleInputTarget) this.visibleInputTarget.removeEventListener("change", this.updateVisible)
    window.removeEventListener("profile-page-designer:apply-section-content", this.handleApplySectionContent)
    window.removeEventListener("profile-page-designer:apply-all-sections-content", this.handleApplyAllSectionsContent)
    window.removeEventListener("profile-page-designer:snapshot-all-sections", this.handleSnapshotAllSections)
    window.removeEventListener("profile-page-designer:request-selection", this.handleSelectionRequest)
    window.removeEventListener("profile-page-designer:activate-section", this.handleActivateSection)
    window.removeEventListener("profile-page-designer:restructure-page", this.handleRestructurePage)
    window.removeEventListener("profile-page-designer:restore-all-sections", this.handleRestoreAllSections)
    window.removeEventListener("profile-page-designer:update-images", this.handleUpdateImages)
    if (this.hasPreviewFrameTarget) {
      this.previewFrameTarget.removeEventListener("dblclick", this.startInlineEditingFromPreview)
      this.previewFrameTarget.removeEventListener("focusout", this.handleInlineBlur)
      this.previewFrameTarget.removeEventListener("keydown", this.handleInlineKeydown)
      this.previewFrameTarget.removeEventListener("paste", this.handleInlinePaste)
    }
    if (this.hasInspectorFieldsTarget) {
      this.inspectorFieldsTarget.removeEventListener("focusin", this.handleInspectorFocusIn)
      this.inspectorFieldsTarget.removeEventListener("focusout", this.handleInspectorFocusOut)
    }
    if (this.hasHeaderPreviewFrameTarget) this.headerPreviewFrameTarget.removeEventListener("load", this.handleHeaderPreviewLoad)
    if (this.hasFooterPreviewFrameTarget) this.footerPreviewFrameTarget.removeEventListener("load", this.handleFooterPreviewLoad)
    document.removeEventListener("keydown", this.handleGlobalKeydown)
  }

  renderInspector() {
    if (this.selectedSurface === "header") {
      this.sidebarEmptyTarget.classList.add("hidden")
      this.sidebarContentTarget.classList.add("hidden")
      this.footerSidebarContentTarget.classList.add("hidden")
      this.headerSidebarContentTarget.classList.remove("hidden")
      this.renderHeaderInspector()
      this.applySelectionState()
      this.applyLinkedFocusState()
      return
    }

    if (this.selectedSurface === "footer") {
      this.sidebarEmptyTarget.classList.add("hidden")
      this.sidebarContentTarget.classList.add("hidden")
      this.headerSidebarContentTarget.classList.add("hidden")
      this.footerSidebarContentTarget.classList.remove("hidden")
      this.renderFooterInspector()
      this.applySelectionState()
      this.applyLinkedFocusState()
      return
    }

    const section = this.selectedSection()

    if (!section) {
      this.sidebarEmptyTarget.classList.remove("hidden")
      this.sidebarContentTarget.classList.add("hidden")
      this.headerSidebarContentTarget.classList.add("hidden")
      this.footerSidebarContentTarget.classList.add("hidden")
      return
    }

    this.sidebarEmptyTarget.classList.add("hidden")
    this.sidebarContentTarget.classList.remove("hidden")
    this.headerSidebarContentTarget.classList.add("hidden")
    this.footerSidebarContentTarget.classList.add("hidden")

    this.titleInputTarget.value = section.title || ""
    this.visibleInputTarget.checked = !!section.visible
    this.typeSelectTarget.innerHTML = this.sectionTypes.map((type) => {
      const selected = type === section.section_type ? " selected" : ""
      return `<option value="${this.escapeHtml(type)}"${selected}>${this.escapeHtml(this.labelFor(type))}</option>`
    }).join("")

    this.deleteButtonLabelTarget.textContent = section._destroy ? this.msgDeleteCancelLabelValue : this.msgDeleteLabelValue

    if (section.persisted && section.id) {
      this.editLinkTarget.href = this.editUrlTemplateValue.replace("__SECTION_ID__", section.id)
      this.editLinkTarget.classList.remove("opacity-50", "pointer-events-none")
    } else {
      this.editLinkTarget.href = "#"
      this.editLinkTarget.classList.add("opacity-50", "pointer-events-none")
    }

    if (this.hasHeroStyleActionTarget) {
      this.heroStyleActionTarget.classList.toggle("hidden", section.section_type !== "hero")
    }

    if (this.hasReplaceActionTarget) {
      this.replaceActionTarget.classList.toggle("hidden", section.section_type === "hero" || section._destroy)
    }

    this.renderInspectorFields(section)
    this.applySelectionState()
    this.applyLinkedFocusState()
  }

  renderHeaderInspector() {
    const enabled = this.headerEnabled()
    const styleLabel = this.header.header_style === "full" ? this.msgHeaderStyleFullValue : (this.header.header_style === "simple" ? this.msgHeaderStyleSimpleValue : this.msgHeaderStyleNoneValue)
    const logoText = this.header.header_data?.logo_text || this.msgLogoNotSetValue
    const ctaText = this.header.header_data?.cta_text || "CTA 없음"

    this.headerSummaryTitleTarget.textContent = enabled ? styleLabel : this.msgHeaderStyleNoneValue
    this.headerSummaryLogoTarget.textContent = logoText
    this.headerSummaryCtaTarget.textContent = ctaText
  }

  renderFooterInspector() {
    const enabled = this.footerEnabled()
    const styleLabel = this.footer.footer_style === "full" ? this.msgFooterStyleFullValue : (this.footer.footer_style === "simple" ? this.msgFooterStyleSimpleValue : this.msgFooterStyleNoneValue)
    const logoText = this.footer.footer_data?.logo_text || this.msgLogoNotSetValue
    const copyright = this.footer.footer_data?.copyright || this.msgNoCopyrightValue

    this.footerSummaryTitleTarget.textContent = enabled ? styleLabel : this.msgFooterStyleNoneValue
    this.footerSummaryLogoTarget.textContent = logoText
    this.footerSummaryCopyrightTarget.textContent = copyright
  }

  renderInspectorFields(section) {
    const schema = this.fieldSchemaFor(section.section_type)
    if (schema.length === 0) {
      this.inspectorFieldsTarget.innerHTML = `
        <div class="ppd-field">
          <p class="ppd-help">이 섹션 타입은 구조 편집만 지원합니다. 세부 내용은 고급 편집 화면에서 이어서 수정하세요.</p>
        </div>
      `
      return
    }

    this.inspectorFieldsTarget.innerHTML = schema.map((field) => this.renderField(field, section.content_data[field.name], field.name, section.client_key)).join("")
  }

  renderField(field, value, path, clientKey) {
    const label = `<label>${this.escapeHtml(field.label)}</label>`
    const safePath = this.escapeHtml(path)
    const wrapperStart = `<div class="ppd-field ppd-field-shell" data-client-key="${this.escapeHtml(clientKey)}" data-field-path="${safePath}">`
    const wrapperEnd = `</div>`

    switch (field.type) {
      case "textarea":
        return `
          ${wrapperStart}
            ${label}
            <textarea class="ppd-textarea" rows="${field.rows || 3}" data-path="${safePath}" data-action="input->profile-page-designer#updateField change->profile-page-designer#updateField" placeholder="${this.escapeHtml(field.placeholder || "")}">${this.escapeHtml(value || "")}</textarea>
          ${wrapperEnd}
        `
      case "select":
        return `
          ${wrapperStart}
            ${label}
            <select class="ppd-select" data-path="${safePath}" data-action="change->profile-page-designer#updateField">
              ${(field.options || []).map(([text, optionValue]) => {
                const selected = String(optionValue) === String(value ?? "") ? " selected" : ""
                return `<option value="${this.escapeHtml(optionValue)}"${selected}>${this.escapeHtml(text)}</option>`
              }).join("")}
            </select>
          ${wrapperEnd}
        `
      case "checkbox":
        return `
          ${wrapperStart}
            <label class="inline-flex items-center gap-2 cursor-pointer">
              <input type="checkbox" ${value ? "checked" : ""} data-path="${safePath}" data-action="change->profile-page-designer#updateField">
              <span class="text-sm text-gray-700">${this.escapeHtml(field.label)}</span>
            </label>
          ${wrapperEnd}
        `
      case "repeater":
        return this.renderRepeaterField(field, value, path, clientKey)
      default:
        return `
          ${wrapperStart}
            ${label}
            <input type="text" class="ppd-input" value="${this.escapeHtml(value || "")}" data-path="${safePath}" data-action="input->profile-page-designer#updateField change->profile-page-designer#updateField" placeholder="${this.escapeHtml(field.placeholder || "")}">
          ${wrapperEnd}
        `
    }
  }

  renderRepeaterField(field, value, path, clientKey) {
    const items = Array.isArray(value) ? value : []
    const list = items.map((item, index) => `
      <div class="ppd-repeater__item">
        <div class="ppd-repeater__controls">
          <strong class="text-sm text-gray-900">${this.escapeHtml(field.label)} ${index + 1}</strong>
          <button type="button"
                  class="btn-secondary text-xs"
                  data-repeater-name="${this.escapeHtml(field.name)}"
                  data-index="${index}"
                  data-action="profile-page-designer#removeRepeaterItem">
            삭제
          </button>
        </div>
        ${(field.fields || []).map((nestedField) => this.renderField(nestedField, item?.[nestedField.name], `${path}.${index}.${nestedField.name}`, clientKey)).join("")}
      </div>
    `).join("")

    const disableAdd = field.limit && items.length >= field.limit

    return `
      <div class="ppd-field">
        <div class="flex items-center justify-between gap-3 mb-2">
          <label class="mb-0">${this.escapeHtml(field.label)}</label>
          <button type="button"
                  class="btn-secondary text-xs ${disableAdd ? "opacity-50 pointer-events-none" : ""}"
                  data-repeater-name="${this.escapeHtml(field.name)}"
                  data-limit="${field.limit || ""}"
                  data-action="profile-page-designer#addRepeaterItem">
            ${this.escapeHtml(field.add_label || this.msgFieldAddLabelValue)}
          </button>
        </div>
        <div class="ppd-repeater">${list || '<p class="ppd-help">아직 항목이 없습니다.</p>'}</div>
      </div>
    `
  }

  renderHeaderModal() {
    if (this.hasHeaderShowInputTarget) this.headerShowInputTarget.checked = !!this.header.show_header
    if (this.hasHeaderLogoInputTarget) this.headerLogoInputTarget.value = this.header.header_data?.logo_text || ""
    if (this.hasHeaderCtaTextInputTarget) this.headerCtaTextInputTarget.value = this.header.header_data?.cta_text || ""
    if (this.hasHeaderCtaUrlInputTarget) this.headerCtaUrlInputTarget.value = this.header.header_data?.cta_url || ""
    if (this.hasHeaderMenuInputTarget) this.headerMenuInputTarget.checked = this.header.header_data?.menu_enabled !== false

    const previewTemplate = this.headerModalPreviewTemplate()
    if (this.hasHeaderModalTemplateTitleTarget) {
      this.headerModalTemplateTitleTarget.textContent = previewTemplate?.name || this.msgHeaderModalTitleValue
    }
    if (this.hasHeaderModalTemplateDescriptionTarget) {
      this.headerModalTemplateDescriptionTarget.textContent = previewTemplate?.description || this.msgHeaderModalDescriptionValue
    }
    if (this.hasHeaderPreviewFrameTarget) {
      const nextSrc = previewTemplate?.preview_url || ""
      if (this.headerPreviewFrameTarget.src !== nextSrc) {
        this.headerPreviewFrameTarget.src = nextSrc
      } else {
        this.syncHeaderPreviewFrameHeight()
      }
    }
    if (this.hasHeaderPreviewFrameWrapTarget) {
      this.headerPreviewFrameWrapTarget.dataset.device = this.headerModalDevice
    }
    if (this.hasHeaderModalBodyTarget) {
      this.headerModalBodyTarget.dataset.device = this.headerModalDevice
    }

    this.element.querySelectorAll("[data-header-template-id]").forEach((card) => {
      card.classList.toggle("is-selected", card.dataset.headerTemplateId === String(this.headerModalPreviewTemplateId || this.header.template_id || ""))
    })

    this.element.querySelectorAll(".ppd-header-modal__device").forEach((button) => {
      button.classList.toggle("is-active", button.dataset.device === this.headerModalDevice)
    })

    this.syncHeaderPreviewFrameHeight()
  }

  renderFooterModal() {
    this.element.querySelectorAll("[data-footer-path]").forEach((input) => {
      const path = input.dataset.footerPath
      const value = this.getNestedValue(this.footer, path)

      if (input.type === "checkbox") {
        input.checked = !!value
      } else {
        input.value = value || ""
      }
    })

    const previewTemplate = this.footerModalPreviewTemplate()
    if (this.hasFooterModalTemplateTitleTarget) {
      this.footerModalTemplateTitleTarget.textContent = previewTemplate?.name || this.msgFooterModalTitleValue
    }
    if (this.hasFooterModalTemplateDescriptionTarget) {
      this.footerModalTemplateDescriptionTarget.textContent = previewTemplate?.description || this.msgFooterModalDescriptionValue
    }
    if (this.hasFooterPreviewFrameTarget) {
      const nextSrc = previewTemplate?.preview_url || ""
      if (this.footerPreviewFrameTarget.src !== nextSrc) {
        this.footerPreviewFrameTarget.src = nextSrc
      } else {
        this.syncFooterPreviewFrameHeight()
      }
    }
    if (this.hasFooterPreviewFrameWrapTarget) {
      this.footerPreviewFrameWrapTarget.dataset.device = this.footerModalDevice
    }
    if (this.hasFooterModalBodyTarget) {
      this.footerModalBodyTarget.dataset.device = this.footerModalDevice
    }

    this.element.querySelectorAll("[data-footer-template-id]").forEach((card) => {
      card.classList.toggle("is-selected", card.dataset.footerTemplateId === String(this.footerModalPreviewTemplateId || this.footer.template_id || ""))
    })

    this.element.querySelectorAll(".ppd-footer-modal__device").forEach((button) => {
      button.classList.toggle("is-active", button.dataset.device === this.footerModalDevice)
    })

    this.syncFooterPreviewFrameHeight()
  }

  renderSectionReplaceModal() {
    const section = this.replaceModalSectionKey ? this.sectionByKey(this.replaceModalSectionKey) : this.selectedSection()
    const sectionLabel = section ? (section.title || this.labelFor(section.section_type)) : this.msgSectionFallbackValue
    const sectionTypeLabel = section ? this.labelFor(section.section_type) : this.msgSectionTypeFallbackValue

    if (this.hasReplaceModalTemplateTitleTarget) {
      this.replaceModalTemplateTitleTarget.textContent = `${sectionLabel} 교체`
    }
    if (this.hasReplaceModalTemplateDescriptionTarget) {
      this.replaceModalTemplateDescriptionTarget.textContent = `${sectionTypeLabel}에 적용할 템플릿을 선택하면 섹션 타입, 제목, 내용이 즉시 바뀝니다.`
    }

    if (this.hasReplaceModalBodyTarget) {
      this.element.querySelectorAll("[data-section-template-id]").forEach((card) => {
        card.classList.toggle("is-selected", card.dataset.sectionTemplateId === String(this.replaceModalPreviewTemplateId || ""))
      })
    }
  }

  startInlineEditing(editable, { moveCaretToEnd = false } = {}) {
    if (!editable) return

    const clientKey = editable.dataset.clientKey
    const fieldPath = editable.dataset.fieldPath
    if (!clientKey || !fieldPath) return

    if (this.activeInlineFieldPath && (this.activeInlineFieldPath !== fieldPath || this.activeInlineSectionKey !== clientKey)) {
      const current = this.previewFrameTarget.querySelector("[data-inline-editing='true']")
      if (current) this.commitInlineEditing(current)
    }

    this.selectedSurface = "section"
    this.selectedKey = clientKey
    this.activeInlineFieldPath = fieldPath
    this.activeInlineSectionKey = clientKey
    this.activeInspectorFieldPath = fieldPath
    editable.dataset.inlineEditing = "true"
    editable.dataset.originalValue = this.getNestedValue(this.sectionByKey(clientKey)?.content_data || {}, fieldPath) || ""
    editable.classList.add("ppd-inline-text--editing")
    editable.setAttribute("contenteditable", "true")
    editable.focus()
    if (moveCaretToEnd) {
      this.placeCaretAtEnd(editable)
    }
    this.renderInspector()
    this.scrollInspectorFieldIntoView(clientKey, fieldPath)
  }

  commitInlineEditing(editable) {
    const clientKey = editable.dataset.clientKey
    const fieldPath = editable.dataset.fieldPath
    const section = this.sectionByKey(clientKey)
    if (!section || !fieldPath) return

    const multiline = editable.dataset.inlineMultiline === "true"
    const nextValue = this.normalizeInlineText(editable.innerText, multiline)
    this.setNestedValue(section.content_data, fieldPath, nextValue)

    editable.removeAttribute("contenteditable")
    editable.dataset.inlineEditing = "false"
    editable.classList.remove("ppd-inline-text--editing")
    delete editable.dataset.originalValue

    this.activeInlineFieldPath = null
    this.activeInlineSectionKey = null
    this.syncHiddenFields()
    this.renderInspector()
    this.emitSelectionChanged()
    this.queuePreviewRefresh()
  }

  cancelInlineEditing(editable) {
    const originalValue = editable.dataset.originalValue || ""
    editable.textContent = originalValue
    editable.removeAttribute("contenteditable")
    editable.dataset.inlineEditing = "false"
    editable.classList.remove("ppd-inline-text--editing")
    delete editable.dataset.originalValue
    this.activeInlineFieldPath = null
    this.activeInlineSectionKey = null
    this.applyLinkedFocusState()
  }

  restoreInlineFocus() {
    if (!this.pendingInlineFocus?.clientKey) return

    const firstEditable = this.findFirstEditable(this.pendingInlineFocus.clientKey)
    if (firstEditable) {
      this.startInlineEditing(firstEditable, { moveCaretToEnd: !!this.pendingInlineFocus.moveCaretToEnd })
    }

    this.pendingInlineFocus = null
    this.applyLinkedFocusState()
  }

  handleInspectorFocusIn = (event) => {
    const wrapper = event.target.closest("[data-field-path][data-client-key]")
    if (!wrapper || !this.inspectorFieldsTarget.contains(wrapper)) return

    this.activeInspectorFieldPath = wrapper.dataset.fieldPath
    this.selectedKey = wrapper.dataset.clientKey || this.selectedKey
    this.applyLinkedFocusState()
    this.scrollPreviewFieldIntoView(this.selectedKey, this.activeInspectorFieldPath)
  }

  handleInspectorFocusOut = (event) => {
    window.setTimeout(() => {
      if (this.inspectorFieldsTarget.contains(document.activeElement)) return
      if (this.activeInlineFieldPath) return

      this.activeInspectorFieldPath = null
      this.applyLinkedFocusState()
    }, 0)
  }

  findFirstEditable(clientKey) {
    return this.previewFrameTarget.querySelector(`[data-client-key="${this.escapeSelector(clientKey)}"] [data-inline-editable='true']`)
  }

  queuePreviewRefresh({ immediate = false } = {}) {
    window.clearTimeout(this.previewTimer)
    if (immediate) {
      this.performPreviewRefresh()
      return
    }

    this.previewTimer = window.setTimeout(() => this.performPreviewRefresh(), 120)
  }

  buildPreviewPayload() {
    const formData = new FormData()
    formData.append("authenticity_token", this.csrfToken())
    formData.append("selected_section_key", this.selectedKey || "")
    formData.append("selected_surface", this.selectedSurface || "section")
    this.appendFormValue(formData, "header", this.serializableHeader())
    this.appendFormValue(formData, "footer", this.serializableFooter())
    this.serializableSections().forEach((section, index) => {
      this.appendFormValue(formData, `sections[${index}]`, section)
    })
    return formData
  }

  syncHiddenFields() {
    const inputs = []
    this.appendHiddenInputs(inputs, "header", this.serializableHeader())
    this.appendHiddenInputs(inputs, "footer", this.serializableFooter())
    this.serializableSections({ includeDeletedDrafts: false }).forEach((section, index) => {
      this.appendHiddenInputs(inputs, `sections[${index}]`, section)
    })
    this.hiddenFieldsTarget.innerHTML = inputs.join("")
  }

  serializableHeader() {
    return {
      show_header: this.header.show_header ? "1" : "0",
      header_style: this.header.header_style || "none",
      template_id: this.header.template_id || "",
      header_data: this.deepClone(this.header.header_data || {}),
      header_data_json: JSON.stringify(this.header.header_data || {})
    }
  }

  serializableFooter() {
    return {
      show_footer: this.footer.show_footer ? "1" : "0",
      footer_style: this.footer.footer_style || "none",
      template_id: this.footer.template_id || "",
      footer_data: this.deepClone(this.footer.footer_data || {}),
      footer_data_json: JSON.stringify(this.footer.footer_data || {})
    }
  }

  serializableSections({ includeDeletedDrafts = true } = {}) {
    return this.sections
      .filter((section) => includeDeletedDrafts || !(section._destroy && !section.persisted && !section.id))
      .map((section, index) => ({
        id: section.id || "",
        client_key: section.client_key,
        title: section.title || "",
        section_type: section.section_type,
        visible: section.visible ? "1" : "0",
        position: String(index),
        _destroy: section._destroy ? "1" : "0",
        style_variant: section.style_variant || "",
        content_data: this.deepClone(section.content_data || {}),
        content_data_json: JSON.stringify(section.content_data || {})
      }))
  }

  appendHiddenInputs(inputs, prefix, value) {
    if (Array.isArray(value)) {
      value.forEach((nestedValue, index) => this.appendHiddenInputs(inputs, `${prefix}[${index}]`, nestedValue))
      return
    }

    if (value && typeof value === "object") {
      Object.entries(value).forEach(([key, nestedValue]) => this.appendHiddenInputs(inputs, `${prefix}[${key}]`, nestedValue))
      return
    }

    inputs.push(`<input type="hidden" name="${this.escapeHtml(prefix)}" value="${this.escapeHtml(value ?? "")}">`)
  }

  appendFormValue(formData, prefix, value) {
    if (Array.isArray(value)) {
      value.forEach((nestedValue, index) => this.appendFormValue(formData, `${prefix}[${index}]`, nestedValue))
      return
    }

    if (value && typeof value === "object") {
      Object.entries(value).forEach(([key, nestedValue]) => this.appendFormValue(formData, `${prefix}[${key}]`, nestedValue))
      return
    }

    formData.append(prefix, value ?? "")
  }

  updateCount() {
    const count = this.visibleSections().length
    this.countLabelTarget.textContent = `(${count}개)`
  }

  normalizePositions() {
    this.sections.forEach((section, index) => {
      section.position = index
    })

    if (!this.selectedSection()) {
      this.selectedKey = this.sections.find((section) => !section._destroy)?.client_key || null
    }
  }

  visibleSections() {
    return this.sections.filter((section) => !section._destroy)
  }

  selectedSection() {
    return this.sections.find((section) => section.client_key === this.selectedKey) || null
  }

  linkedFocusFieldPath() {
    return this.activeInlineFieldPath || this.activeInspectorFieldPath
  }

  linkedFocusSectionKey() {
    return this.activeInlineSectionKey || (this.linkedFocusFieldPath() ? this.selectedKey : null)
  }

  sectionByKey(clientKey) {
    return this.sections.find((section) => section.client_key === clientKey) || null
  }

  buildNewSection(sectionType) {
    const clientKey = `tmp-${this.nextTempId++}`
    return this.normalizeSection({
      id: null,
      client_key: clientKey,
      title: this.labelFor(sectionType),
      section_type: sectionType,
      visible: true,
      position: this.sections.length,
      persisted: false,
      _destroy: false,
      content_data: this.starterContentFor(sectionType)
    }, this.sections.length)
  }

  normalizeSection(section, index) {
    return {
      id: section.id || null,
      client_key: section.client_key || `tmp-${index + 1}`,
      title: section.title || this.labelFor(section.section_type),
      section_type: section.section_type,
      visible: this.booleanValue(section.visible),
      position: index,
      persisted: !!section.persisted || !!section.id,
      _destroy: this.booleanValue(section._destroy),
      content_data: this.deepClone(section.content_data || {}),
      style_variant: section.style_variant || null
    }
  }

  uniqueTypes() {
    const collected = [
      ...(this.schema.all_types || []),
      ...(this.schema.picker_types || []),
      ...(this.schema.fallback_types || []),
      ...(this.schema.editable_types || []),
      ...this.sections.map((section) => section.section_type)
    ]

    return [...new Set(collected.filter(Boolean))]
  }

  fieldSchemaFor(sectionType) {
    return this.schema.field_schemas?.[sectionType] || []
  }

  starterContentFor(sectionType) {
    return this.deepClone(this.schema.starter_content?.[sectionType] || {})
  }

  blankRepeaterItem(fields) {
    return (fields || []).reduce((memo, field) => {
      memo[field.name] = field.type === "checkbox" ? false : ""
      return memo
    }, {})
  }

  setNestedValue(target, path, value) {
    const keys = path.split(".")
    let cursor = target

    keys.forEach((key, index) => {
      const isLast = index === keys.length - 1
      const nextKey = keys[index + 1]
      const nextIsIndex = /^\d+$/.test(nextKey)

      if (isLast) {
        cursor[key] = value
        return
      }

      if (cursor[key] == null) {
        cursor[key] = nextIsIndex ? [] : {}
      }

      cursor = cursor[key]
    })
  }

  getNestedValue(target, path) {
    return path.split(".").reduce((current, key) => {
      if (current == null) return null
      return current[key]
    }, target)
  }

  labelFor(sectionType) {
    return this.labels?.[sectionType] || sectionType
  }

  booleanValue(value) {
    return value === true || value === "true" || value === "1" || value === 1
  }

  bumpTempId(clientKey) {
    const match = String(clientKey || "").match(/^tmp-(\d+)$/)
    if (match) {
      this.nextTempId = Math.max(this.nextTempId, Number(match[1]) + 1)
    }
  }

  csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content ||
      this.element.querySelector("input[name='authenticity_token']")?.value ||
      ""
  }

  snapshot() {
    return JSON.stringify({
      header: this.serializableHeader(),
      footer: this.serializableFooter(),
      sections: this.sections.map((section) => ({
        id: section.id || null,
        client_key: section.client_key,
        title: section.title || "",
        section_type: section.section_type,
        visible: !!section.visible,
        position: section.position,
        _destroy: !!section._destroy,
        content_data: section.content_data || {}
      }))
    })
  }

  isDirty() {
    return this.snapshot() !== this.initialSnapshot
  }

  parseJson(value, fallback) {
    try {
      return JSON.parse(value || "null") ?? fallback
    } catch (_error) {
      return fallback
    }
  }

  deepClone(value) {
    return JSON.parse(JSON.stringify(value))
  }

  normalizeHeader(header) {
    return {
      show_header: this.booleanValue(header.show_header ?? true),
      header_style: header.header_style || "none",
      template_id: header.template_id ? String(header.template_id) : "",
      header_data: this.deepClone(header.header_data || {})
    }
  }

  normalizeFooter(footer) {
    return {
      show_footer: this.booleanValue(footer.show_footer ?? true),
      footer_style: footer.footer_style || "none",
      template_id: footer.template_id ? String(footer.template_id) : "",
      footer_data: this.deepClone(footer.footer_data || {})
    }
  }

  normalizeInlineText(value, multiline) {
    const normalized = String(value || "").replace(/\u00a0/g, " ")
    if (multiline) {
      return normalized.replace(/\n{3,}/g, "\n\n").trim()
    }

    return normalized.replace(/\s+/g, " ").trim()
  }

  placeCaretAtEnd(element) {
    const selection = window.getSelection()
    const range = document.createRange()
    range.selectNodeContents(element)
    range.collapse(false)
    selection.removeAllRanges()
    selection.addRange(range)
  }

  removeSection(clientKey) {
    const section = this.sections.find((item) => item.client_key === clientKey)
    if (!section) return

    if (section.persisted || section.id) {
      section._destroy = !section._destroy
    } else {
      this.sections = this.sections.filter((item) => item.client_key !== clientKey)
    }

    if (this.selectedKey === clientKey) {
      this.selectedKey = this.sections.find((item) => !item._destroy && item.client_key !== clientKey)?.client_key ||
        this.sections.find((item) => !item._destroy)?.client_key ||
        null
    }
    if (this.selectedSurface === "section" && !this.selectedKey) {
      this.selectedSurface = "header"
    }

    this.normalizePositions()
    this.renderInspector()
    this.syncHiddenFields()
    this.updateCount()
    this.emitSelectionChanged()
    this.queuePreviewRefresh({ immediate: true })
  }

  handleSelectionRequest = () => {
    this.emitSelectionChanged("request")
  }

  handleActivateSection = (event) => {
    const requestedKey = event.detail?.clientKey
    const targetSection = requestedKey ? this.sectionByKey(requestedKey) : this.sections.find((section) => !section._destroy)
    if (!targetSection) return

    this.selectedSurface = "section"
    this.selectedKey = targetSection.client_key
    this.renderInspector()
    this.applySelectionState()
    this.scrollSelectionIntoView()
    this.emitSelectionChanged("user")
  }

  handleApplySectionContent = (event) => {
    const payload = event.detail?.data
    if (!payload || this.selectedSurface !== "section") return

    const section = this.selectedSection()
    if (!section) return

    if (payload.title !== undefined) {
      section.title = String(payload.title || "").trim()
    }

    // __generate__ 값을 빈 문자열로 치환 — 이미지 생성 전 프리뷰 404 방지
    const safeContentData = this.stripGeneratePlaceholders(payload.content_data)

    section.content_data = this.mergeSectionContentData(
      section.content_data || {},
      safeContentData,
      this.fieldSchemaFor(section.section_type)
    )

    this.renderInspector()
    this.syncHiddenFields()
    this.emitSelectionChanged()
    this.queuePreviewRefresh({ immediate: true })
  }

  handleSnapshotAllSections = (event) => {
    const snapshot = this.visibleSections().map(s => ({
      section_type: s.section_type,
      title: s.title,
      content_data: s.content_data ? JSON.parse(JSON.stringify(s.content_data)) : {},
      style_variant: s.style_variant || null
    }))
    if (event.detail?.callback) event.detail.callback(snapshot)
  }

  handleApplyAllSectionsContent = (event) => {
    const incomingSections = event.detail?.sections
    if (!Array.isArray(incomingSections) || incomingSections.length === 0) return

    console.debug("[ProfilePageDesigner] apply-all-sections-content received", incomingSections)

    for (const incoming of incomingSections) {
      const match = this.visibleSections().find(s => s.section_type === incoming.section_type)
      if (!match) {
        console.debug("[ProfilePageDesigner] no match for section_type", incoming.section_type)
        continue
      }

      if (incoming.style_variant) {
        console.debug("[ProfilePageDesigner] applying style_variant to", match.section_type, incoming.style_variant)
        match.style_variant = incoming.style_variant
      }

      if (incoming.content_data && typeof incoming.content_data === "object") {
        match.content_data = this.mergeSectionContentData(
          match.content_data || {},
          incoming.content_data,
          this.fieldSchemaFor(match.section_type)
        )
      }

      if (incoming.title !== undefined) {
        match.title = String(incoming.title || "").trim()
      }
    }

    this.renderInspector()
    this.syncHiddenFields()
    this.emitSelectionChanged()
    this.queuePreviewRefresh({ immediate: true })
  }

  handleRestructurePage = (event) => {
    if (event.detail?.refreshOnly) {
      this.syncHiddenFields()
      this.queuePreviewRefresh({ immediate: true })
      return
    }

    const sections = event.detail?.sections
    if (!Array.isArray(sections) || sections.length === 0) return

    console.debug("[ProfilePageDesigner] restructure-page received", sections)

    for (const entry of sections) {
      switch (entry.op) {
        case "keep": {
          const section = this.sectionByKey(entry.client_key)
          if (!section) break
          if (entry.style_variant) section.style_variant = entry.style_variant
          if (entry.content_data && typeof entry.content_data === "object") {
            section.content_data = this.mergeSectionContentData(
              section.content_data || {},
              entry.content_data,
              this.fieldSchemaFor(section.section_type)
            )
          }
          if (entry.title !== undefined) section.title = String(entry.title).trim()
          break
        }
        case "replace": {
          const section = this.sectionByKey(entry.client_key)
          if (!section) break
          const newType = entry.new_section_type || entry.section_type
          if (newType) section.section_type = newType
          section.title = entry.title || this.labelFor(newType) || section.title
          section.style_variant = entry.style_variant || null
          section.content_data = this.stripGeneratePlaceholders(entry.content_data) || this.starterContentFor(newType)
          break
        }
        case "remove": {
          const section = this.sectionByKey(entry.client_key)
          if (section) section._destroy = true
          break
        }
        case "add": {
          const newSection = this.buildNewSection(entry.section_type)
          if (entry.title) newSection.title = entry.title
          if (entry.style_variant) newSection.style_variant = entry.style_variant
          if (entry.content_data) newSection.content_data = this.stripGeneratePlaceholders(entry.content_data)
          const pos = entry.position ?? this.visibleSections().length
          this.sections.splice(pos, 0, newSection)
          break
        }
      }
    }

    // 헤더 템플릿 적용
    const headerData = event.detail?.header
    if (headerData?.template_id) {
      const template = this.headerTemplateById(String(headerData.template_id))
      if (template) {
        console.debug("[ProfilePageDesigner] applying header template", template.id, template.name)
        this.header.show_header = true
        this.header.header_style = template.header_style
        this.header.template_id = String(template.id)
        this.header.header_data = this.deepClone(template.header_data || {})
        this.header.header_data.template_id = String(template.id)
        if (this.header.header_data.menu_enabled == null) this.header.header_data.menu_enabled = true
      }
    }

    // 푸터 템플릿 적용
    const footerData = event.detail?.footer
    if (footerData?.template_id) {
      const template = this.footerTemplateById(String(footerData.template_id))
      if (template) {
        console.debug("[ProfilePageDesigner] applying footer template", template.id, template.name)
        this.footer.show_footer = true
        this.footer.footer_style = template.footer_style
        this.footer.template_id = String(template.id)
        this.footer.footer_data = this.deepClone(template.footer_data || {})
        this.footer.footer_data.template_id = String(template.id)
      }
    }

    this.normalizePositions()
    this.renderInspector()
    this.syncHiddenFields()
    this.updateCount()
    this.emitSelectionChanged()
    this.queuePreviewRefresh({ immediate: true })
  }

  handleRestoreAllSections = (event) => {
    const snapshot = event.detail?.snapshot
    if (!Array.isArray(snapshot)) return

    console.debug("[ProfilePageDesigner] restore-all-sections received", snapshot)

    // Remove sections added during restructure
    const visible = this.visibleSections()
    for (let i = snapshot.length; i < visible.length; i++) {
      visible[i]._destroy = true
    }

    // Restore snapshotted sections
    const currentVisible = this.visibleSections()
    for (let i = 0; i < snapshot.length; i++) {
      const saved = snapshot[i]
      if (!saved || i >= currentVisible.length) continue

      currentVisible[i].section_type = saved.section_type
      currentVisible[i].title = saved.title
      currentVisible[i].content_data = JSON.parse(JSON.stringify(saved.content_data || {}))
      currentVisible[i].style_variant = saved.style_variant || null
      currentVisible[i]._destroy = false
    }

    this.normalizePositions()
    this.renderInspector()
    this.syncHiddenFields()
    this.updateCount()
    this.emitSelectionChanged()
    this.queuePreviewRefresh({ immediate: true })
  }

  // __generate__ placeholder를 빈 문자열로 치환 — 프리뷰 404 방지
  stripGeneratePlaceholders(contentData) {
    if (!contentData || typeof contentData !== "object") return contentData
    const cd = { ...contentData }
    for (const [key, val] of Object.entries(cd)) {
      if (val === "__generate__") {
        cd[key] = ""
      } else if (Array.isArray(val)) {
        cd[key] = val.map(item => {
          if (!item || typeof item !== "object") return item
          const clone = { ...item }
          for (const [k, v] of Object.entries(clone)) {
            if (v === "__generate__") clone[k] = ""
          }
          return clone
        })
      }
    }
    return cd
  }

  handleUpdateImages = (event) => {
    const updates = event.detail?.updates
    if (!Array.isArray(updates) || updates.length === 0) return

    for (const update of updates) {
      const { clientKey, field, value, arrayKey, index } = update
      const section = this.sections.find(s => s.client_key === clientKey)
      if (!section?.content_data) continue

      if (arrayKey != null && index != null) {
        if (Array.isArray(section.content_data[arrayKey]) && section.content_data[arrayKey][index]) {
          section.content_data[arrayKey][index][field] = value
        }
      } else {
        section.content_data[field] = value
      }
    }

    this.syncHiddenFields()
    this.queuePreviewRefresh({ immediate: true })
  }

  headerEnabled() {
    return !!this.header.show_header && this.header.header_style && this.header.header_style !== "none"
  }

  footerEnabled() {
    return !!this.footer.show_footer && this.footer.footer_style && this.footer.footer_style !== "none"
  }

  headerTemplateById(id) {
    if (!id) return null
    return this.headerTemplates.find((template) => String(template.id) === String(id)) || null
  }

  footerTemplateById(id) {
    if (!id) return null
    return this.footerTemplates.find((template) => String(template.id) === String(id)) || null
  }

  ensureHeaderModalPreviewTemplate() {
    if (this.headerModalPreviewTemplateId && this.headerTemplateById(this.headerModalPreviewTemplateId)) return
    if (this.header.template_id && this.headerTemplateById(this.header.template_id)) {
      this.headerModalPreviewTemplateId = String(this.header.template_id)
      return
    }

    const match = this.headerTemplates.find((template) => template.header_style === this.header.header_style)
    this.headerModalPreviewTemplateId = String(match?.id || this.headerTemplates[0]?.id || "")
  }

  ensureFooterModalPreviewTemplate() {
    if (this.footerModalPreviewTemplateId && this.footerTemplateById(this.footerModalPreviewTemplateId)) return
    if (this.footer.template_id && this.footerTemplateById(this.footer.template_id)) {
      this.footerModalPreviewTemplateId = String(this.footer.template_id)
      return
    }

    const match = this.footerTemplates.find((template) => template.footer_style === this.footer.footer_style)
    this.footerModalPreviewTemplateId = String(match?.id || this.footerTemplates[0]?.id || "")
  }

  headerModalPreviewTemplate() {
    return this.headerTemplateById(this.headerModalPreviewTemplateId)
  }

  footerModalPreviewTemplate() {
    return this.footerTemplateById(this.footerModalPreviewTemplateId)
  }

  sectionTemplateById(id) {
    if (!id) return null
    return this.sectionTemplates.find((template) => String(template.id) === String(id)) || null
  }

  applySectionReplacement(section, { sectionType, title, styleVariant, contentData }) {
    if (sectionType && sectionType !== section.section_type) {
      section.section_type = sectionType
    }
    if (title) section.title = title
    section.style_variant = styleVariant || null
    section.content_data = contentData || this.starterContentFor(sectionType || section.section_type)
  }

  applySectionReplacementTemplate(template) {
    const section = this.replaceModalSectionKey ? this.sectionByKey(this.replaceModalSectionKey) : this.selectedSection()
    if (!section || section.section_type === "hero") return

    this.applySectionReplacement(section, {
      sectionType: template.section_type || section.section_type,
      title: template.title || this.labelFor(template.section_type || section.section_type) || section.title,
      styleVariant: template.style_variant,
      contentData: template.content_data || {}
    })
    section._destroy = false

    this.selectedSurface = "section"
    this.selectedKey = section.client_key
    this.replaceModalSectionKey = section.client_key
    this.replaceModalPreviewTemplateId = String(template.id)
    this.renderInspector()
    this.renderSectionReplaceModal()
    this.syncHiddenFields()
    this.updateCount()
    this.emitSelectionChanged("user")
    this.queuePreviewRefresh({ immediate: true })
  }

  handleGlobalKeydown = (event) => {
    if (event.key === "Escape" && this.headerModalOpen) {
      this.closeHeaderModal()
    }
    if (event.key === "Escape" && this.footerModalOpen) {
      this.closeFooterModal()
    }
    if (event.key === "Escape" && this.heroModalOpen) {
      this.closeHeroModal()
    }
    if (event.key === "Escape" && this.replaceModalOpen) {
      this.closeSectionReplaceModal()
    }
  }

  handleHeaderPreviewLoad = () => {
    this.syncHeaderPreviewFrameHeight()
  }

  handleFooterPreviewLoad = () => {
    this.syncFooterPreviewFrameHeight()
  }

  syncHeaderPreviewFrameHeight() {
    if (!this.hasHeaderPreviewFrameTarget) return

    window.requestAnimationFrame(() => {
      const frame = this.headerPreviewFrameTarget
      const doc = frame.contentDocument || frame.contentWindow?.document
      if (!doc) return

      const body = doc.body
      const html = doc.documentElement
      const root = doc.querySelector(".preview-header-wrap") || doc.querySelector("#portfolio-header")
      const rootHeight = root?.getBoundingClientRect?.().height || 0
      const documentHeight = Math.max(
        body?.scrollHeight || 0,
        body?.offsetHeight || 0,
        html?.scrollHeight || 0,
        html?.offsetHeight || 0
      )
      const measuredHeight = this.headerModalDevice === "mobile" ? Math.max(Math.ceil(rootHeight), documentHeight) : Math.ceil(rootHeight || documentHeight)
      const nextHeight = this.headerModalDevice === "mobile" ? Math.max(measuredHeight, 640) : Math.max(measuredHeight, 72)
      frame.style.height = `${nextHeight}px`
    })
  }

  syncFooterPreviewFrameHeight() {
    if (!this.hasFooterPreviewFrameTarget) return

    window.requestAnimationFrame(() => {
      const frame = this.footerPreviewFrameTarget
      const doc = frame.contentDocument || frame.contentWindow?.document
      if (!doc) return

      const body = doc.body
      const html = doc.documentElement
      const root = doc.querySelector(".preview-footer-wrap") || doc.querySelector("footer")
      const rootHeight = root?.getBoundingClientRect?.().height || 0
      const documentHeight = Math.max(
        body?.scrollHeight || 0,
        body?.offsetHeight || 0,
        html?.scrollHeight || 0,
        html?.offsetHeight || 0
      )
      const measuredHeight = this.footerModalDevice === "mobile" ? Math.max(Math.ceil(rootHeight), documentHeight) : Math.ceil(rootHeight || documentHeight)
      const nextHeight = this.footerModalDevice === "mobile" ? Math.max(measuredHeight, 640) : Math.max(measuredHeight, 72)
      frame.style.height = `${nextHeight}px`
    })
  }

  applySelectionState() {
    if (this.hasPreviewFrameTarget) {
      this.previewFrameTarget.querySelectorAll(".ppd-canvas-section--active").forEach((node) => node.classList.remove("ppd-canvas-section--active"))
      this.previewFrameTarget.querySelectorAll(".ppd-preview-header--active").forEach((node) => node.classList.remove("ppd-preview-header--active"))
    }

    if (this.selectedSurface === "header") {
      this.previewFrameTarget.querySelectorAll(".ppd-preview-header").forEach((node) => node.classList.add("ppd-preview-header--active"))
      return
    }

    if (this.selectedSurface === "footer") {
      this.previewFrameTarget.querySelectorAll(".ppd-preview-footer").forEach((node) => node.classList.add("ppd-preview-header--active"))
      return
    }

    if (!this.selectedKey) return
    this.findPreviewSectionNode(this.selectedKey)?.classList.add("ppd-canvas-section--active")
  }

  scrollSelectionIntoView() {
    if (this.selectedSurface === "section" && this.selectedKey) {
      const node = this.findPreviewSectionNode(this.selectedKey)
      this.scrollNodeIntoViewport(node)
      this.titleInputTarget?.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" })
      return
    }

    if (this.selectedSurface === "header") {
      const headerNode = this.previewFrameTarget.querySelector(".ppd-preview-header")
      this.scrollNodeIntoViewport(headerNode)
      return
    }

    if (this.selectedSurface === "footer") {
      const footerNode = this.previewFrameTarget.querySelector(".ppd-preview-footer")
      this.scrollNodeIntoViewport(footerNode)
    }
  }

  scrollNodeIntoViewport(node) {
    if (!node) return

    const nodeRect = node.getBoundingClientRect()
    const viewportOffset = Math.max((window.innerHeight - nodeRect.height) / 2, 120)
    const nextTop = window.scrollY + nodeRect.top - viewportOffset

    window.scrollTo({
      top: Math.max(0, nextTop),
      behavior: "smooth"
    })
  }

  applyLinkedFocusState() {
    if (this.hasPreviewFrameTarget) {
      this.previewFrameTarget.querySelectorAll(".is-linked-focus").forEach((node) => node.classList.remove("is-linked-focus"))
      this.previewFrameTarget.querySelectorAll(".is-linked-section").forEach((node) => node.classList.remove("is-linked-section"))
    }

    if (this.hasInspectorFieldsTarget) {
      this.inspectorFieldsTarget.querySelectorAll(".is-linked-focus").forEach((node) => node.classList.remove("is-linked-focus"))
    }

    const fieldPath = this.linkedFocusFieldPath()
    const sectionKey = this.linkedFocusSectionKey()
    if (!fieldPath || !sectionKey) return

    const previewNode = this.findPreviewFieldNode(sectionKey, fieldPath)
    const previewSection = this.findPreviewSectionNode(sectionKey)
    const inspectorNode = this.findInspectorFieldNode(sectionKey, fieldPath)

    previewNode?.classList.add("is-linked-focus")
    previewSection?.classList.add("is-linked-section")
    inspectorNode?.classList.add("is-linked-focus")
  }

  findPreviewFieldNode(clientKey, fieldPath) {
    return this.previewFrameTarget.querySelector(
      `[data-inline-editable='true'][data-client-key="${this.escapeSelector(clientKey)}"][data-field-path="${this.escapeSelector(fieldPath)}"]`
    )
  }

  findPreviewSectionNode(clientKey) {
    return this.previewFrameTarget.querySelector(`.ppd-canvas-section[data-client-key="${this.escapeSelector(clientKey)}"]`)
  }

  findInspectorFieldNode(clientKey, fieldPath) {
    return this.inspectorFieldsTarget.querySelector(
      `[data-client-key="${this.escapeSelector(clientKey)}"][data-field-path="${this.escapeSelector(fieldPath)}"]`
    )
  }

  scrollPreviewFieldIntoView(clientKey, fieldPath) {
    const node = this.findPreviewFieldNode(clientKey, fieldPath)
    node?.scrollIntoView({ block: "center", inline: "nearest", behavior: "smooth" })
  }

  scrollInspectorFieldIntoView(clientKey, fieldPath) {
    const node = this.findInspectorFieldNode(clientKey, fieldPath)
    node?.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" })
  }

  emitSelectionChanged(interactionSource = "sync") {
    const section = this.selectedSurface === "section" ? this.selectedSection() : null
    const detail = {
      selectedSurface: this.selectedSurface || "section",
      clientKey: section?.client_key || null,
      id: section?.id || null,
      sectionType: section?.section_type || null,
      title: section?.title || null,
      contentData: this.deepClone(section?.content_data || {}),
      fieldSchema: this.deepClone(section ? this.fieldSchemaFor(section.section_type) : []),
      label: section ? this.labelFor(section.section_type) : null,
      visible: section ? !!section.visible : null,
      interactionSource
    }

    window.dispatchEvent(new CustomEvent("profile-page-designer:selection-changed", { detail }))
  }

  mergeSectionContentData(currentContent, incomingContent, fieldSchema) {
    const nextContent = this.deepClone(currentContent || {})
    if (!incomingContent || typeof incomingContent !== "object" || Array.isArray(incomingContent)) return nextContent

    const schemaByName = new Map((fieldSchema || []).map((field) => [field.name, field]))
    Object.entries(incomingContent).forEach(([key, value]) => {
      const field = schemaByName.get(key)
      if (!field) return

      if (field.type === "repeater") {
        if (!Array.isArray(value)) return
        nextContent[key] = this.deepClone(value)
        return
      }

      nextContent[key] = this.deepClone(value)
    })

    return nextContent
  }

  escapeHtml(value) {
    return String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;")
  }

  escapeSelector(value) {
    if (window.CSS?.escape) return window.CSS.escape(value)
    return String(value).replace(/"/g, '\\"')
  }
}
